import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing, Plus } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import { isAdmin as isAdminHelper, MANAGER_ROLES, ADMIN_ROLES } from "../utils/role";
import { approveBonus, cancelBonus, payBonus, sendBonusReminders } from "../api/bonuses";
import { useBonuses } from "../features/bonuses/useBonuses";
import { BonusFilters } from "../features/bonuses/BonusFilters";
import { BonusForm } from "../features/bonuses/BonusForm";
import { BonusTable } from "../features/bonuses/BonusTable";
import { BonusDetail } from "../features/bonuses/BonusDetail";
import { BonusReportCard } from "../features/bonuses/BonusReportCard";

const CREATORS = [...MANAGER_ROLES, "hr", "general_manager"];
const PAYERS = [...MANAGER_ROLES, "hr", "accountant", "general_manager"];

export default function BonusesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const state = useBonuses();
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const isAdmin = isAdminHelper(user);

  const run = async (fn, key) => {
    try {
      await fn();
      toast.success(t(`bonus.done.${key}`));
      state.reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === "string" ? detail : t("bonus.actionFailed")));
    }
  };

  const cancel = (row) => {
    const reason = window.prompt(t("bonus.actions.cancelReason"));
    if (reason && reason.trim().length >= 10) run(() => cancelBonus(row.bonus_id, reason.trim()), "cancelled");
    else if (reason !== null) toast.error(t("bonus.actions.cancelReasonShort"));
  };

  return (
    <AppShell
      title={t("bonus.title")}
      subtitle={t("bonus.subtitle")}
      actions={
        <div className="flex flex-wrap gap-2">
          {isAdmin ? (
            <NeuButton data-testid="bonus-send-reminders-btn" className="flex items-center gap-2"
                       onClick={() => run(sendBonusReminders, "reminded")}>
              <BellRing className="h-4 w-4" /> {t("bonus.remind")}
            </NeuButton>
          ) : null}
          {CREATORS.includes(user?.role) ? (
            <NeuButton variant="primary" data-testid="bonus-issue-btn" className="flex items-center gap-2"
                       onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> {t("bonus.issue")}
            </NeuButton>
          ) : null}
        </div>
      }
    >
      <div className="grid gap-5" data-testid="bonuses-page">
        <BonusFilters filters={state.filters} setFilters={state.setFilters}
                      employees={state.employees} branches={state.branches} />
        {creating ? (
          <BonusForm employees={state.employees} boxes={state.boxes}
                     onSaved={state.reload} onClose={() => setCreating(false)} />
        ) : null}
        <BonusTable
          rows={state.rows}
          isAdmin={isAdmin}
          canPay={PAYERS.includes(user?.role)}
          onApprove={(row) => run(() => approveBonus(row.bonus_id), "approved")}
          onPay={(row) => run(() => payBonus(row.bonus_id), "paid")}
          onCancel={cancel}
          onView={(row) => setViewing(row.bonus_id)}
        />
        <BonusReportCard filters={state.filters} />
      </div>
      <BonusDetail bonusId={viewing} onClose={() => setViewing(null)} />
    </AppShell>
  );
}
